import { useCallback, useEffect, useState } from "react";
import {
  getMySubmissions,
  getStudentSubmissionFolders,
  getSubmissionContext,
  getTeacherSubmissionFolders,
} from "../services";

export const SUBMISSION_FOLDERS_UPDATED_EVENT = "sooth-submission-folders-updated";

const asList = (value, keys = []) => {
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return [];
};

export function useTeacherSubmissionFolders() {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTeacherSubmissionFolders();
      setFolders(asList(data, ["folders", "submissionFolders", "items"]));
      setError(null);
      return data;
    } catch (requestError) {
      setError(requestError);
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  return { folders, setFolders, loading, error, refresh };
}

export function useStudentSubmissions(collectionId) {
  const [folders, setFolders] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    try {
      const [folderData, submissionData] = await Promise.all([
        getStudentSubmissionFolders(),
        getMySubmissions(),
      ]);
      setFolders(asList(folderData, ["folders", "submissionFolders", "items"]));
      const submissionList = asList(submissionData, ["submissions", "items"]);
      setSubmissions(
        submissionList.filter(
          (submission) => Number(
            submission.collectionID_lng ?? submission.collectionID ?? submission.collection_id
          ) === Number(collectionId)
        )
      );
      setError(null);
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, [collectionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadDetail = useCallback((submissionId) => {
    return getSubmissionContext(submissionId);
  }, []);

  return { folders, submissions, loading, error, refresh, loadDetail };
}
